// ─────────────────────────────────────────────────────────────────────────────
// PQFORCE Mobile — the installations this device knows about. Pure.
//
// ⚠ MULTI-TENANT IS SUPPORTED, and this is why the remembered address is a LIST
// rather than a single string. Real people work across installations — the
// design's own example has one person on "Helvetia Group · SSO" and "ANDRITZ
// Soutec · email login" — and a single remembered address would silently
// overwrite one with the other on every switch.
//
// ⚠ NOTHING HERE IS A CREDENTIAL. A host, a display name and which sign-in
// method was chosen last time. The bearer token never leaves the portal, and the
// device credential is an httpOnly cookie this code cannot read. That is what
// makes it safe to keep this list in localStorage at all.
// ─────────────────────────────────────────────────────────────────────────────

export interface KnownTenant {
    /** The address as typed, alias included — it is what pairing needs. */
    url: string;
    /** The bare host, for display and for de-duplication. */
    host: string;
    /** The tenant's own name once discovery has given us one. */
    displayName?: string;
    /** Which method was used last time, so a re-auth can offer it directly. */
    methodId?: string;
    methodTitle?: string;
    /** Unix ms. Decides the order, and which one is "active". */
    lastUsed: number;
}

/**
 * Add or refresh an entry, keyed by HOST.
 *
 * Keyed by host and not by the typed string, or `demo.pqforce.com` and
 * `https://demo.pqforce.com/` would sit in the list as two installations — the
 * same normalisation problem `pqfNormalizeTenantUrlKey()` solves on the PHP side
 * for the same reason.
 */
export function upsertTenant(list: readonly KnownTenant[], tenant: KnownTenant): KnownTenant[] {
    const rest = list.filter((t) => t.host !== tenant.host);
    const previous = list.find((t) => t.host === tenant.host);
    // Merge, so a re-pair that learns nothing new does not erase what we knew.
    const merged: KnownTenant = {
        ...previous,
        ...tenant,
        displayName: tenant.displayName ?? previous?.displayName,
        methodId: tenant.methodId ?? previous?.methodId,
        methodTitle: tenant.methodTitle ?? previous?.methodTitle,
    };
    return sortTenants([...rest, merged]);
}

/** Most recently used first — the one a returning user almost always wants. */
export function sortTenants(list: readonly KnownTenant[]): KnownTenant[] {
    return [...list].sort((a, b) => b.lastUsed - a.lastUsed);
}

/** The one a returning user lands on, or null when this device knows none. */
export function activeTenant(list: readonly KnownTenant[]): KnownTenant | null {
    return sortTenants(list)[0] ?? null;
}

/** Remove one, by host. */
export function forgetTenant(list: readonly KnownTenant[], host: string): KnownTenant[] {
    return list.filter((t) => t.host !== host);
}

/**
 * Parse whatever localStorage held.
 *
 * Anything unrecognised yields an empty list rather than throwing: this runs on
 * the first paint, and a corrupted convenience must never stop the app booting.
 * It also absorbs the earlier single-string format, so an upgrade does not make
 * a returning user retype their address.
 */
export function parseKnownTenants(raw: string | null): KnownTenant[] {
    if (!raw) return [];

    // The pre-list format: a bare address string.
    //
    // ⚠ It must LOOK like a host before it is read as one. Anything else stored
    // under this key — a JSON object from a future format, or plain corruption —
    // would otherwise become an "installation" whose host is that text, and the
    // returning screen would offer it as somewhere to sign in.
    if (!raw.startsWith("[")) {
        const url = raw.trim();
        const host = url
            .replace(/^https?:\/\//i, "")
            .replace(/\/.*$/, "")
            .toLowerCase();
        if (!/^[a-z0-9.-]+\.[a-z]{2,}$/i.test(host)) return [];
        return [{ url, host, lastUsed: 0 }];
    }

    try {
        const parsed: unknown = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return sortTenants(
            parsed
                .filter(
                    (t): t is KnownTenant =>
                        !!t &&
                        typeof t === "object" &&
                        typeof (t as KnownTenant).url === "string" &&
                        typeof (t as KnownTenant).host === "string",
                )
                .map((t) => ({ ...t, lastUsed: typeof t.lastUsed === "number" ? t.lastUsed : 0 })),
        );
    } catch {
        return [];
    }
}

/** The subtitle on an installation row: who it is and how you get in. */
export function tenantRowSubtitle(tenant: KnownTenant): string {
    const parts = [tenant.displayName, tenant.methodTitle].filter(Boolean);
    return parts.length ? parts.join(" · ") : tenant.host;
}
